import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import * as React from 'react';

import type { AuthorizedProfileDto } from '@/api/negotiation-client';
import type {
  OperationalDetailRowDto,
  RateSeniorityLevelDto,
  SupplierRowDto,
} from '@/api/operational-view-client';
import {
  useCards,
  useSupplierDetail,
  useSupplierList,
} from '@/api/use-operational-view';
import { useNegotiationAuthState, useReopenNegotiation } from '@/api/use-negotiation';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FilterCombo, type FilterComboOption } from '@/components/ui/filter-combo';
import { Label } from '@/components/ui/label';
import { Panel } from '@/components/ui/panel';
import { filterTriggerLabel } from '@/lib/filter-trigger-label';
import {
  coupleSeniorityLevels,
  negotiationCoupleKey,
  negotiationStateByCouple,
  type NegotiationCoupleState,
} from '@/lib/negotiation-couple';
import {
  HORS_JEUX_EXPLANATION,
  formatEur,
  seniorityLabel,
} from '@/lib/operational-view-labels';
import { formatSiret } from '@/routes/admin/campaigns/campaign-suppliers-focus';
import { OperationalViewHeading } from '@/routes/admin/operational-view/operational-view-cards';
import {
  OperationalViewComparisonPanel,
  OperationalViewComparisonToggle,
} from '@/routes/admin/operational-view/operational-view-comparison-panel';
import { useDisplayPreferences } from '@/routes/admin/operational-view/operational-view-preferences';
import {
  ColumnsMenu,
  EmptyCell,
  HorsJeuBadge,
  OperationalTable,
  PanelFooter,
  PanelMessage,
  PanelToolbar,
  StaleCycleBadge,
  toTableState,
} from '@/routes/admin/operational-view/operational-view-table';

// Ids des colonnes MASQUABLES de la liste (Section 2). `supplierSiret` (en-tête de ligne / sélection)
// reste TOUJOURS visible (enableHiding:false) ⇒ hors de cette liste.
const SUPPLIER_HIDEABLE_IDS = [
  'raisonSociale',
  'responseRatePct',
  'horsJeuxProfileCount',
  'belowP25Count',
  'withinP25P75Count',
  'aboveP75Count',
  'lastResponseAt',
  'cycleStale',
] as const;

/** Colonnes chiffrées de la liste (valeurs alignées à droite, tabular-nums). */
const SUPPLIER_NUMERIC_IDS: ReadonlySet<string> = new Set([
  'responseRatePct',
  'horsJeuxProfileCount',
  'belowP25Count',
  'withinP25P75Count',
  'aboveP75Count',
  'lastResponseAt',
]);

/** Id de la région « Comparaison avec les campagnes antérieures » (cible aria-controls du lien de pied). */
const COMPARISON_PANEL_ID = 'opv-fournisseurs-comparison';

// =============================================================================
// operational-view-fournisseurs-screen.tsx — Page Fournisseurs §2.5 v2.2 (S14-bis / C2).
// =============================================================================
// Symétrique de la page Profils (ex-« Grille détaillée »). L'information Hors Jeux / cycle ne passe
// JAMAIS par une couleur seule (Cx-3, WCAG 1.4.1) : LIBELLÉ TEXTE explicite, couleur en renfort.
//
//   §1 — Cards d'indicateurs (réutilisables, OperationalCardsPanel).
//   §2 — Liste de TOUS les fournisseurs invités (useSupplierList) : TanStack Table. Filtre RAISON
//        SOCIALE = recherche libre wildcard FRONT-SIDE (sur la liste déjà chargée, au fil de la
//        frappe ; AUCUN appel réseau par frappe) + toggle « cycle non actualisé ». Clic ligne → §3.
//   §3 — Détail du fournisseur sélectionné (useSupplierDetail, à la demande) : par couple, C1/C2 +
//        écarts + évolution. Une ligne Hors Jeux porte un badge LIBELLÉ « Hors Jeux »
//        (voile de fond SOLIDE amber-50 = renfort visuel redondant, jamais le seul porteur).
//
// L'AUTORITÉ reste l'API (calcul AU VOL) ; l'écran LIT et HABILLE. État IN-MEMORY React (RULE_REF_006).
//
// RESTYLING 2026-09-17 (spec tasks/spec-restyling-vue-operationnelle-20260917.md, GATE_HUMAN « simple
// restyling ») — même structure que la page Profils : h2 sr-only, bascule + 4 indicateurs, card Panel
// « Fournisseurs invités » + compteur (champ « Raison sociale… », « Fournisseurs à actualiser seulement »,
// Colonnes n / n ▾), SIRET groupé (présentation seule), pied « Comparer aux campagnes antérieures », détail
// sous la liste UNIQUEMENT à la sélection (raison sociale + SIRET, Colonnes ▾, Fermer).
// Les bandes percentile (< P25 / [P25-P75] / > P75) arrivent en COMPTEURS (jamais le percentile en colonne).
//
//   §3-bis — OUVERTURE DE NÉGOCIATION (décision GATE_HUMAN du 2026-09-22, verbatim : « il faut déplacer
//        seulement la fonctionnalité négociation sur la page […]/operational-view/fournisseurs »). Elle
//        vivait dans la colonne « Négo. » (⇄) du ranking de l'onglet Analyse ; elle vit MAINTENANT ici,
//        dans le détail du fournisseur. POURQUOI ICI : « La vue opérationnelle doit permettre de
//        effectuer toutes les analyses et de renégocier les TJM proposés par les fournisseurs s'ils ne
//        sont pas compétitifs » (même échange). C'est sur CET écran que l'admin lit, par profil et par
//        fournisseur, le TJM et son écart ; la décision de renégocier s'y prend, elle doit donc pouvoir
//        s'y exécuter. C'est un DÉPLACEMENT, pas une duplication : l'onglet Analyse ne l'offre plus.
//
//        GRAIN INCHANGÉ (décision du 2026-09-17) : ouvrir depuis UNE ligne ouvre le BINÔME
//        fournisseur × profil, donc TOUS les niveaux de séniorité du profil. Le libellé de l'action ET
//        la confirmation le DISENT, en nommant les niveaux : l'écran liste des lignes Profil / Niveau,
//        l'utilisateur croirait sinon n'ouvrir que le niveau cliqué. L'ouverture est IDEMPOTENTE et
//        RÉPÉTABLE (mode `add`) : elle ne referme jamais rien, donc l'action reste offerte sur un
//        binôme déjà ouvert. Le VERROUILLAGE reste au grain FOURNISSEUR (décision du 2026-09-18) et
//        reste porté par l'onglet Négociation : rien de cela ne change ici.
//
//        AUCUN ENDPOINT NOUVEAU : `useNegotiationAuthState` (état relayé) et `useReopenNegotiation`
//        (`POST …/negotiation/reopen`, mode `add`) sont EXACTEMENT le client et le hook qu'appelait
//        l'ancienne colonne ⇄. L'API reste l'AUTORITÉ (cohorte, garde per-profil, refus 409 d'un
//        binôme déjà verrouillé) ; l'écran relaie une intention, il ne décide de rien.

/** Formate un montant + son pourcentage (« 350 € (54 %) ») ou tiret si null. */
function formatEurPct(eur: number | null, pct: number | null): string {
  if (eur === null) {
    return '—';
  }
  const pctSuffix = pct === null ? '' : ` (${String(pct)} %)`;
  return `${formatEur(eur)}${pctSuffix}`;
}

/** Date de réponse (ISO) → date FR courte (partie date de l'ISO, déterministe) ; « — » si null. */
function formatResponseDate(iso: string | null): string {
  return iso === null ? '—' : iso.slice(0, 10);
}

const supplierColumnHelper = createColumnHelper<SupplierRowDto>();

export interface OperationalViewFournisseursScreenProps {
  readonly campaignId: string;
  /** Navigation vers la page sœur Profils (porté par l'adaptateur routeur). */
  readonly onViewProfils?: () => void;
}

export function OperationalViewFournisseursScreen({
  campaignId,
  onViewProfils,
}: OperationalViewFournisseursScreenProps): React.JSX.Element {
  // Filtre RAISON SOCIALE — lot F1 (GATE_HUMAN 2026-09-21) : le champ de recherche libre devient une
  // LISTE DÉROULANTE AUTOFILTRANTE multi-sélection (motif Google Sheets). Le texte ne disparaît pas,
  // il vit DANS le panneau : on tape pour réduire la liste, on coche ce qu'on garde. La sélection est
  // une liste de SIRET (identifiant stable, deux homonymes restent distincts), état IN-MEMORY
  // (RULE_REF_006 : jamais de localStorage ; la page n'a pas de search params).
  const [selectedRaisons, setSelectedRaisons] = React.useState<readonly string[]>([]);
  // Toggle CLIENT-SIDE « n'afficher que les fournisseurs à actualiser » (cycleStale).
  const [staleOnly, setStaleOnly] = React.useState(false);
  // Fournisseur sélectionné pour le détail §3 (null = aucun ⇒ §3 vide, requête désactivée).
  const [selectedSiret, setSelectedSiret] = React.useState<string | null>(null);

  // §1/§2 sans filtre serveur de raison sociale (la recherche est front-side). Cycle = défaut (tous).
  const noFilters = React.useMemo(() => ({}), []);
  const cardsQuery = useCards(campaignId, noFilters);
  const listQuery = useSupplierList(campaignId, noFilters);

  const detailFilters = React.useMemo(
    () => (selectedSiret === null ? {} : { supplierSiret: selectedSiret }),
    [selectedSiret],
  );
  const detailQuery = useSupplierDetail(
    campaignId,
    detailFilters,
    selectedSiret !== null,
  );

  /** Options de la liste autofiltrante : raison sociale en libellé, SIRET en complément cherchable. */
  const raisonOptions = React.useMemo(
    (): readonly FilterComboOption[] =>
      (listQuery.data?.rows ?? []).map((row) => ({
        value: row.supplierSiret,
        label: row.raisonSociale === '' ? row.supplierSiret : row.raisonSociale,
        hint: row.supplierSiret,
      })),
    [listQuery.data?.rows],
  );
  /** Libellés des fournisseurs cochés — l'état du filtre est dit EN TEXTE par le déclencheur. */
  const selectedRaisonLabels = selectedRaisons.map(
    (siret) => raisonOptions.find((option) => option.value === siret)?.label ?? siret,
  );

  // Filtres CLIENT-SIDE : fournisseurs cochés (aucun = tous) PUIS toggle cycleStale.
  const supplierRows = React.useMemo(() => {
    const rows = listQuery.data?.rows ?? [];
    return rows
      .filter((r) =>
        selectedRaisons.length === 0 ? true : selectedRaisons.includes(r.supplierSiret),
      )
      .filter((r) => (staleOnly ? r.cycleStale : true));
  }, [listQuery.data?.rows, selectedRaisons, staleOnly]);

  const columns = React.useMemo(
    () => [
      supplierColumnHelper.accessor('supplierSiret', {
        id: 'supplierSiret',
        header: 'SIRET',
        // SIRET groupé 3-3-3 + NIC (présentation seule, la valeur triée reste le SIRET brut).
        cell: (info) => formatSiret(info.getValue()),
        // En-tête de ligne + cible de sélection ⇒ toujours visible (non masquable).
        enableHiding: false,
      }),
      supplierColumnHelper.accessor('raisonSociale', {
        id: 'raisonSociale',
        header: 'Raison sociale',
        cell: (info) => (info.getValue() === '' ? '—' : info.getValue()),
      }),
      supplierColumnHelper.accessor('responseRatePct', {
        id: 'responseRatePct',
        header: 'Taux de réponse',
        cell: (info) => `${String(info.getValue())} %`,
      }),
      supplierColumnHelper.accessor('horsJeuxProfileCount', {
        id: 'horsJeuxProfileCount',
        header: 'Hors jeu',
        cell: (info) => String(info.getValue()),
      }),
      supplierColumnHelper.accessor('belowP25Count', {
        id: 'belowP25Count',
        header: '< P25',
        cell: (info) => String(info.getValue()),
      }),
      supplierColumnHelper.accessor('withinP25P75Count', {
        id: 'withinP25P75Count',
        header: '[P25-P75]',
        cell: (info) => String(info.getValue()),
      }),
      supplierColumnHelper.accessor('aboveP75Count', {
        id: 'aboveP75Count',
        header: '> P75',
        cell: (info) => String(info.getValue()),
      }),
      supplierColumnHelper.accessor('lastResponseAt', {
        id: 'lastResponseAt',
        header: 'Date de réponse',
        cell: (info) => formatResponseDate(info.getValue()),
      }),
      supplierColumnHelper.display({
        id: 'cycleStale',
        header: 'Cycle',
        // LIBELLÉ TEXTE (jamais couleur/teinte seule, Cx-3 / WCAG 1.4.1). Cycle à jour ⇒ tiret.
        cell: (ctx) =>
          ctx.row.original.cycleStale ? <StaleCycleBadge /> : <EmptyCell />,
      }),
    ],
    [],
  );

  // Cx-6 : tri + masquage de la Section 2 PERSISTÉS (localStorage v2, clé « fournisseurs »). La recherche
  // raison sociale + toggle stale NE sont PAS persistés (décision GATE_HUMAN C3 : recherche ≠ préférence).
  const [prefs, setPrefs] = useDisplayPreferences(
    'fournisseurs',
    SUPPLIER_HIDEABLE_IDS,
  );
  const { visibility, sorting } = React.useMemo(
    () => toTableState(prefs.hiddenColumns, prefs.sort),
    [prefs.hiddenColumns, prefs.sort],
  );

  const table = useReactTable({
    data: supplierRows as SupplierRowDto[],
    columns,
    state: { sorting, columnVisibility: visibility },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      setPrefs({
        ...prefs,
        sort: next.length === 0 ? null : { id: next[0]!.id, desc: next[0]!.desc },
      });
    },
    onColumnVisibilityChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(visibility) : updater;
      setPrefs({
        ...prefs,
        hiddenColumns: SUPPLIER_HIDEABLE_IDS.filter((id) => next[id] === false),
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Ligne de la liste du fournisseur sélectionné : titre (raison sociale + SIRET) et résumé du détail.
  const selectedRow = React.useMemo(
    () =>
      selectedSiret === null
        ? undefined
        : listQuery.data?.rows.find((r) => r.supplierSiret === selectedSiret),
    [selectedSiret, listQuery.data?.rows],
  );

  // Déclencheur de la sélection (bouton d'en-tête de ligne) : le focus y revient à la fermeture du détail.
  const selectionTriggerRef = React.useRef<HTMLButtonElement | null>(null);
  const selectSupplier = React.useCallback(
    (row: SupplierRowDto, trigger: HTMLButtonElement | null): void => {
      selectionTriggerRef.current = trigger;
      setSelectedSiret(row.supplierSiret);
    },
    [],
  );
  const closeDetail = React.useCallback((): void => {
    setSelectedSiret(null);
    const trigger = selectionTriggerRef.current;
    if (trigger?.isConnected === true) {
      trigger.focus();
    }
  }, []);

  // §4 — Comparaison antérieure : repliée par défaut, dépliée par le lien de pied de liste.
  const [comparisonExpanded, setComparisonExpanded] = React.useState(false);

  return (
    <div className="space-y-6">
      {/* §1 — Titre de page (nom accessible conservé, visuel porté par l'en-tête de zone), bascule, indicateurs. */}
      <OperationalViewHeading
        title="Vue opérationnelle — Fournisseurs"
        switchProps={{ current: 'fournisseurs', onViewProfils }}
        cards={{
          isPending: cardsQuery.isPending,
          isError: cardsQuery.isError,
          errorMessage: cardsQuery.error?.message,
          cards: cardsQuery.data,
        }}
      />

      {/* §2 — Fournisseurs invités : barre (recherche, à actualiser, colonnes) + table (clic ligne → §3). */}
      <Panel
        headingId="opv-fournisseurs-list-heading"
        title="Fournisseurs invités"
        count={listQuery.isSuccess ? supplierRows.length : undefined}
        aside={<p className="max-w-xl text-xs">{HORS_JEUX_EXPLANATION}</p>}
      >
        <PanelToolbar>
          {/* Lot F1 : liste déroulante AUTOFILTRANTE multi-sélection. Le champ de recherche libre
              n'a pas disparu — il est le champ de filtre DU PANNEAU (on tape, on coche). */}
          <FilterCombo
            triggerLabel={filterTriggerLabel('Raison sociale', selectedRaisonLabels)}
            groupLabel="Raison sociale"
            options={raisonOptions}
            selected={selectedRaisons}
            onChange={setSelectedRaisons}
            searchPlaceholder="Raison sociale…"
          />
          <div className="flex items-center gap-2">
            <input
              id="filter-stale-only"
              type="checkbox"
              className="size-4 rounded border-input"
              checked={staleOnly}
              onChange={(e) => setStaleOnly(e.target.checked)}
            />
            <Label htmlFor="filter-stale-only" className="font-normal">
              Fournisseurs à actualiser seulement
            </Label>
          </div>
          <div className="ml-auto">
            {/* Masquage de colonnes (persisté localStorage v2, clé « fournisseurs »). */}
            <ColumnsMenu table={table} idPrefix="fournisseurs-list" />
          </div>
        </PanelToolbar>

        {listQuery.isPending ? (
          <PanelMessage kind="status">Chargement des fournisseurs…</PanelMessage>
        ) : listQuery.isError ? (
          <PanelMessage kind="alert">
            Erreur de chargement : {listQuery.error.message}
          </PanelMessage>
        ) : supplierRows.length === 0 ? (
          <PanelMessage kind="status">
            Aucun fournisseur pour la recherche courante.
          </PanelMessage>
        ) : (
          <OperationalTable
            table={table}
            caption="Liste des fournisseurs invités : SIRET, raison sociale, taux de réponse, nombre de profils Hors Jeu (au-dessus de la médiane du couple + seuil), répartition en bandes (inférieur au P25, entre P25 et P75, supérieur au P75), date de réponse et état du cycle."
            numericColumnIds={SUPPLIER_NUMERIC_IDS}
            rowHeaderClassName="whitespace-nowrap font-mono"
            selection={{
              isSelected: (row) => row.supplierSiret === selectedSiret,
              onSelect: selectSupplier,
            }}
          />
        )}

        <PanelFooter
          action={
            <OperationalViewComparisonToggle
              expanded={comparisonExpanded}
              onToggle={() => setComparisonExpanded((v) => !v)}
              controlsId={COMPARISON_PANEL_ID}
            />
          }
        >
          Taux de réponse, nombre de profils Hors Jeu et répartition en bandes (&lt; P25
          / [P25-P75] / &gt; P75), date de dernière réponse. Sélectionnez une ligne
          pour afficher le détail par couple.
        </PanelFooter>
      </Panel>

      {/* §3 — Détail du fournisseur sélectionné : card absente sans sélection. */}
      {selectedSiret !== null ? (
        <SupplierDetailPanel
          campaignId={campaignId}
          siret={selectedSiret}
          supplierRow={selectedRow}
          isPending={detailQuery.isPending}
          isError={detailQuery.isError}
          errorMessage={detailQuery.error?.message}
          rows={detailQuery.data?.rows}
          onClose={closeDetail}
        />
      ) : null}

      {/* §4 — Comparaison OPTIONNELLE avec les campagnes antérieures (aucune lecture tant que repliée). */}
      <OperationalViewComparisonPanel
        campaignId={campaignId}
        expanded={comparisonExpanded}
        panelId={COMPARISON_PANEL_ID}
      />
    </div>
  );
}

// --- §3 — Détail du fournisseur sélectionné (par couple) ----------------------

interface SupplierDetailPanelProps {
  /** Campagne courante : périmètre des lectures et des commandes de négociation (autorité API). */
  readonly campaignId: string;
  readonly siret: string;
  /** Ligne de la liste du fournisseur (raison sociale + résumé des valeurs déjà affichées). */
  readonly supplierRow: SupplierRowDto | undefined;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly errorMessage: string | undefined;
  readonly rows: readonly OperationalDetailRowDto[] | undefined;
  readonly onClose: () => void;
}

const detailColumnHelper = createColumnHelper<OperationalDetailRowDto>();

/** Contexte de la colonne « Négociation » du détail (état relayé + commande + verrou d'attente). */
interface NegotiationColumnContext {
  /** Raison sociale du fournisseur du panneau (nom accessible de l'action) ; SIRET en repli. */
  readonly supplierName: string;
  /** État par binôme relayé par l'API (`closed` par défaut : rien n'est ouvert tant qu'on ne le sait pas). */
  readonly states: ReadonlyMap<string, NegotiationCoupleState>;
  /** Niveaux du profil présents dans le détail, ordonnés Débutant → Référent (grain d'ouverture). */
  readonly levelsByProfile: ReadonlyMap<string, readonly RateSeniorityLevelDto[]>;
  /** `true` pendant la commande d'ouverture (toutes les actions sont alors désactivées). */
  readonly pending: boolean;
  /** Demande d'ouverture du BINÔME (le niveau de la ligne cliquée n'entre pas dans la commande). */
  readonly onRequestOpen: (profileCode: string) => void;
}

// Colonnes Section 3 page Fournisseurs (ordre DBU l.1179, DISTINCT de Profils §3). Triables + masquables
// (état LOCAL, NON persisté). `Profil/Niveau` = en-tête de ligne, non masquable.
//
// FABRIQUE plutôt que constante de module depuis le 2026-09-22 : la colonne « Négociation » a besoin de
// l'état relayé et de la commande, qui vivent dans le panneau. Les ids restent STABLES (le masquage de
// colonnes les référence).
function buildSupplierDetailColumns(
  negotiation: NegotiationColumnContext,
): ColumnDef<OperationalDetailRowDto>[] {
  return [
  detailColumnHelper.accessor('profileCode', {
    id: 'profil',
    header: 'Profil / Niveau',
    enableHiding: false,
    cell: (info) =>
      `${info.getValue()} · ${seniorityLabel(info.row.original.seniorityLevel)}`,
  }),
  detailColumnHelper.accessor('medianEur', {
    id: 'medianEur',
    header: 'Médiane',
    cell: (info) => formatEur(info.getValue()),
  }),
  detailColumnHelper.accessor('respondentCount', {
    id: 'respondentCount',
    header: 'Nb répondants',
    cell: (info) => String(info.getValue()),
  }),
  detailColumnHelper.accessor('responseC1Eur', {
    id: 'responseC1Eur',
    header: 'Réponse C1',
    cell: (info) => formatEur(info.getValue()),
  }),
  detailColumnHelper.accessor('responseC2Eur', {
    id: 'responseC2Eur',
    header: 'Réponse C2',
    cell: (info) => formatEur(info.getValue()),
  }),
  detailColumnHelper.accessor('evolutionC1C2Eur', {
    id: 'evolutionC1C2Eur',
    header: 'Évolution C1→C2',
    cell: (info) =>
      formatEurPct(info.getValue(), info.row.original.evolutionC1C2Pct),
  }),
  detailColumnHelper.accessor('ecartMedianEur', {
    id: 'ecartMedianEur',
    header: 'Écart Médiane',
    cell: (info) =>
      formatEurPct(info.getValue(), info.row.original.ecartMedianPct),
  }),
  detailColumnHelper.accessor('ecartTargetEur', {
    id: 'ecartTargetEur',
    header: 'Écart Prix Cible',
    cell: (info) =>
      formatEurPct(info.getValue(), info.row.original.ecartTargetPct),
  }),
  // RENOMMAGE 2026-09-22 : « État » → « Hors jeu ». Cette colonne n'a JAMAIS habillé un état de
  // négociation : elle porte le seul badge Hors Jeux. Le voisinage de la colonne « Négociation »
  // rendait l'intitulé générique ambigu (deux « états » côte à côte). Renommage COSMÉTIQUE : ni l'id
  // (`horsJeux`, clé de masquage), ni la donnée, ni la sémantique ne changent.
  detailColumnHelper.display({
    id: 'horsJeux',
    header: 'Hors jeu',
    cell: (ctx) => (ctx.row.original.horsJeux ? <HorsJeuBadge /> : <EmptyCell />),
  }),
  // Colonne d'ACTION (décision GATE_HUMAN du 2026-09-22) : ni triable, ni masquable — masquer une
  // action la rendrait introuvable, et trier sur elle n'a pas de sens.
  detailColumnHelper.display({
    id: 'negotiation',
    header: 'Négociation',
    enableHiding: false,
    enableSorting: false,
    cell: (ctx) => (
      <NegotiationCell
        supplierName={negotiation.supplierName}
        profileCode={ctx.row.original.profileCode}
        levels={negotiation.levelsByProfile.get(ctx.row.original.profileCode) ?? []}
        state={
          negotiation.states.get(
            negotiationCoupleKey(
              ctx.row.original.supplierSiret,
              ctx.row.original.profileCode,
            ),
          ) ?? 'closed'
        }
        pending={negotiation.pending}
        onOpen={() => negotiation.onRequestOpen(ctx.row.original.profileCode)}
      />
    ),
  }),
  ];
}

/** Colonnes chiffrées du détail Fournisseurs (alignées à droite). */
const SUPPLIER_DETAIL_NUMERIC_IDS: ReadonlySet<string> = new Set([
  'medianEur',
  'respondentCount',
  'responseC1Eur',
  'responseC2Eur',
  'evolutionC1C2Eur',
  'ecartMedianEur',
  'ecartTargetEur',
]);

function SupplierDetailPanel({
  campaignId,
  siret,
  supplierRow,
  isPending,
  isError,
  errorMessage,
  rows,
  onClose,
}: SupplierDetailPanelProps): React.JSX.Element {
  // Tri + masquage LOCAUX (non persistés : le détail change à chaque sélection).
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});

  const raisonSociale = supplierRow?.raisonSociale ?? '';
  // Nom du fournisseur pour les libellés d'action : la raison sociale, le SIRET en repli (un nom
  // accessible « Ouvrir la négociation de  sur DATA-ENG » ne désignerait personne).
  const supplierName = raisonSociale === '' ? formatSiret(siret) : raisonSociale;

  // --- Négociation (déménagée ici le 2026-09-22) : LECTURE de l'état + COMMANDE d'ouverture --------
  // Les deux hooks ne sont montés QUE lorsqu'un fournisseur est sélectionné (le panneau n'existe pas
  // autrement) : aucune lecture inutile sur la page tant qu'aucun détail n'est ouvert.
  const authQuery = useNegotiationAuthState(campaignId);
  const reopenNegotiation = useReopenNegotiation(campaignId);
  const negotiationStates = React.useMemo(
    () => negotiationStateByCouple(authQuery.data?.auths),
    [authQuery.data?.auths],
  );
  const detailRows = React.useMemo(() => rows ?? [], [rows]);
  // Niveaux de séniorité PAR PROFIL, dans le périmètre chargé du fournisseur : c'est l'ensemble que
  // l'ouverture couvre, et celui que le libellé doit nommer (grain binôme, décision du 2026-09-17).
  const levelsByProfile = React.useMemo(() => {
    const map = new Map<string, readonly RateSeniorityLevelDto[]>();
    for (const row of detailRows) {
      if (!map.has(row.profileCode)) {
        map.set(row.profileCode, coupleSeniorityLevels(detailRows, row.profileCode));
      }
    }
    return map;
  }, [detailRows]);

  // Binôme en attente de CONFIRMATION (`null` = aucune modale). L'ouverture est irréversible au sens
  // métier (elle ne se referme pas), et elle porte sur plus que la ligne cliquée : elle se confirme.
  const [pendingProfileCode, setPendingProfileCode] = React.useState<string | null>(null);
  // Mémoïsé : `confirmOpenNegotiation` en dépend, et une liste recréée à chaque rendu recréerait le
  // callback à chaque rendu (donc toute la colonne d'actions).
  const pendingLevels = React.useMemo(
    (): readonly RateSeniorityLevelDto[] =>
      pendingProfileCode === null ? [] : (levelsByProfile.get(pendingProfileCode) ?? []),
    [pendingProfileCode, levelsByProfile],
  );

  const confirmOpenNegotiation = React.useCallback((): void => {
    if (pendingProfileCode === null) {
      return;
    }
    // La liste EXPRIME l'intention « tous les niveaux du profil » ; elle ne la borne pas : l'API
    // n'ouvre qu'au grain CODE de profil et ré-attache les niveaux via le ProfileScope figé (A2).
    const profilesAuthorized: readonly AuthorizedProfileDto[] = pendingLevels.map(
      (level) => ({ profileCode: pendingProfileCode, seniorityLevel: level }),
    );
    reopenNegotiation.mutate({
      supplierSiret: siret,
      profilesAuthorized,
      // `add` : ouvrir un binôme ne referme JAMAIS une ouverture déjà posée (A2, décision 9).
      mode: 'add',
    });
    setPendingProfileCode(null);
  }, [pendingProfileCode, pendingLevels, reopenNegotiation, siret]);

  const negotiationColumnContext = React.useMemo(
    (): NegotiationColumnContext => ({
      supplierName,
      states: negotiationStates,
      levelsByProfile,
      pending: reopenNegotiation.isPending,
      onRequestOpen: setPendingProfileCode,
    }),
    [supplierName, negotiationStates, levelsByProfile, reopenNegotiation.isPending],
  );
  const columns = React.useMemo(
    () => buildSupplierDetailColumns(negotiationColumnContext),
    [negotiationColumnContext],
  );

  const table = useReactTable({
    data: detailRows as OperationalDetailRowDto[],
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Résumé mono : valeurs DÉJÀ affichées sur la ligne de la liste (aucune donnée nouvelle).
  const summary =
    supplierRow === undefined
      ? null
      : [
          `Taux de réponse ${String(supplierRow.responseRatePct)} %`,
          `Hors jeu ${String(supplierRow.horsJeuxProfileCount)}`,
          `Date de réponse ${formatResponseDate(supplierRow.lastResponseAt)}`,
        ].join(' · ');

  return (
    <Panel
      headingId="opv-fournisseurs-detail-heading"
      title={
        raisonSociale === '' ? (
          <span className="font-mono">{formatSiret(siret)}</span>
        ) : (
          <>
            {raisonSociale}{' '}
            <span className="font-mono text-sm font-normal text-muted-foreground">
              {formatSiret(siret)}
            </span>
          </>
        )
      }
      aside={
        <>
          {summary !== null ? (
            <span className="font-mono text-xs">{summary}</span>
          ) : null}
          <ColumnsMenu
            table={table}
            idPrefix="fournisseurs-detail"
            withCount={false}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            className="text-foreground"
          >
            Fermer
          </Button>
        </>
      }
    >
      {/* Refus de l'API (hors cohorte, binôme déjà verrouillé…) : dit EN TEXTE, jamais avalé. */}
      {reopenNegotiation.isError ? (
        <PanelMessage kind="alert">
          Ouverture de négociation refusée : {reopenNegotiation.error.message}
        </PanelMessage>
      ) : null}

      {isPending ? (
        <PanelMessage kind="status">Chargement du détail…</PanelMessage>
      ) : isError ? (
        <PanelMessage kind="alert">Erreur de chargement : {errorMessage}</PanelMessage>
      ) : detailRows.length === 0 ? (
        <PanelMessage kind="status">
          Aucun couple renseigné pour ce fournisseur.
        </PanelMessage>
      ) : (
        <OperationalTable
          table={table}
          caption="Détail par couple Profil × Niveau du fournisseur sélectionné : médiane du couple, nombre de répondants, réponse C1, réponse C2, évolution C1→C2, écart à la médiane, écart au Prix Cible (repère), état Hors Jeux (au-dessus de la médiane du couple + seuil) et ouverture de la négociation du binôme Fournisseur × Profil."
          numericColumnIds={SUPPLIER_DETAIL_NUMERIC_IDS}
          isOutOfPlay={(row) => row.horsJeux}
        />
      )}

      {/* Confirmation : l'ouverture porte sur PLUS que la ligne cliquée, elle se dit avant de se faire. */}
      <ConfirmDialog
        open={pendingProfileCode !== null}
        title="Ouvrir la négociation du profil"
        description={
          pendingProfileCode === null
            ? ''
            : `${supplierName} · profil ${pendingProfileCode}. L’ouverture porte sur TOUS les niveaux de séniorité du profil (${levelsSentence(pendingLevels)}), pas sur la seule ligne sélectionnée. Elle ne referme aucune ouverture déjà posée.`
        }
        confirmLabel="Ouvrir tous les niveaux du profil"
        onConfirm={confirmOpenNegotiation}
        onCancel={() => setPendingProfileCode(null)}
      />

      <PanelFooter>
        Détail par couple Profil × Niveau du fournisseur sélectionné : réponses C1/C2,
        écarts à la médiane et au Prix Cible (repère), évolution C1→C2. Ouvrir la
        négociation depuis une ligne ouvre le binôme Fournisseur × Profil entier, donc
        tous ses niveaux de séniorité. Colonnes triables et masquables.
      </PanelFooter>
    </Panel>
  );
}

/** Énumère des niveaux en libellés FR (« Débutant, Expert ») ; liste vide ⇒ mention explicite. */
function levelsSentence(levels: readonly RateSeniorityLevelDto[]): string {
  return levels.length === 0
    ? 'aucun niveau chargé'
    : levels.map((level) => seniorityLabel(level)).join(', ');
}

/**
 * Cellule « Négociation » du détail Fournisseurs (déplacée de la colonne « Négo. » du ranking le
 * 2026-09-22). Trois états relayés par l'API, TOUJOURS doublés d'un LIBELLÉ TEXTE (jamais la couleur
 * seule, Cx-3 / WCAG 1.4.1) :
 *   • `closed`  ⇒ « À ouvrir », action « Ouvrir » ;
 *   • `open`    ⇒ badge « Ouverte », action « Rouvrir » TOUJOURS ACTIVE (l'ouverture est idempotente
 *                 et répétable : elle ajoute, elle ne referme rien) ;
 *   • `locked`  ⇒ badge « Verrouillée ». L'action reste offerte : la garde est l'AUTORITÉ de l'API
 *                 (409 NEGOTIATION_ALREADY_LOCKED), jamais une déduction de l'écran, qui lit un état
 *                 potentiellement périmé.
 *
 * WCAG 2.5.3 (« label in name ») : le libellé VISIBLE (« Ouvrir » / « Rouvrir ») est le premier mot du
 * nom accessible. Celui-ci nomme le FOURNISSEUR, le PROFIL et les NIVEAUX couverts : depuis une liste
 * de lignes Profil / Niveau, un simple « Ouvrir » laisserait croire à une ouverture du seul niveau.
 */
function NegotiationCell({
  supplierName,
  profileCode,
  levels,
  state,
  pending,
  onOpen,
}: {
  readonly supplierName: string;
  readonly profileCode: string;
  readonly levels: readonly RateSeniorityLevelDto[];
  readonly state: NegotiationCoupleState;
  readonly pending: boolean;
  readonly onOpen: () => void;
}): React.JSX.Element {
  const scope = `tous les niveaux du profil : ${levelsSentence(levels)}`;
  const verb = state === 'closed' ? 'Ouvrir' : 'Rouvrir';
  const label =
    state === 'closed'
      ? `Ouvrir la négociation de ${supplierName} sur ${profileCode} (${scope})`
      : state === 'open'
        ? `Rouvrir la négociation de ${supplierName} sur ${profileCode} (déjà ouverte, ${scope}) : rouvrir ne referme rien`
        : `Rouvrir la négociation de ${supplierName} sur ${profileCode} (verrouillée, ${scope}) : l’API refusera tant que le verrou tient`;

  return (
    <div className="flex items-center gap-2">
      <NegotiationStateBadge state={state} />
      <Button
        type="button"
        variant={state === 'closed' ? 'outline' : 'ghost'}
        size="sm"
        aria-label={label}
        title={label}
        disabled={pending}
        onClick={onOpen}
      >
        {verb}
      </Button>
    </div>
  );
}

/** État d'ouverture du binôme en LIBELLÉ TEXTE (la teinte n'est qu'un renfort, Cx-3 / WCAG 1.4.1). */
function NegotiationStateBadge({
  state,
}: {
  readonly state: NegotiationCoupleState;
}): React.JSX.Element {
  if (state === 'closed') {
    return <span className="text-xs text-muted-foreground">À ouvrir</span>;
  }
  return (
    <Badge
      variant="outline"
      className="whitespace-nowrap border-foreground/30 text-foreground"
    >
      {state === 'open' ? 'Ouverte' : 'Verrouillée'}
    </Badge>
  );
}
